import { DynamoDB } from 'aws-sdk';
import { isEqual } from 'lodash';
import {
  DynamoDBTable,
  DynamoDBKeySchemaElement,
  DynamoDBAttributeDefinition,
  DynamoDBAttributeValue,
} from '@localstack/types';

export type DynamoDBTypedItem = Record<string, DynamoDBAttributeValue>;
export type DynamoDBPlainItem = Record<string, unknown>;

/**
 * When working with items by index we need to "restore" original type of the
 * value, otherwise request may fail due to key schema mismatch
 * (i.e. when passing string range key instead of a number etc.)
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
export const restoreDynamoDBType = <T>(type: Optional<string>, value: any): T => {
  if (type === 'S') return value?.toString();
  if (value && type === 'N') return Number.parseFloat(value.toString()) as unknown as T;
  if (type === 'BOOL') return Boolean(Number.parseInt(value?.toString(), 10)) as unknown as T;

  return value;
};

interface ExtractDynamoDBPrimaryKeyReturn {
  hashKey: Optional<DynamoDBKeySchemaElement>;
  rangeKey: Optional<DynamoDBKeySchemaElement>;
  hashKeyDef: Optional<DynamoDBAttributeDefinition>;
  rangeKeyDef: Optional<DynamoDBAttributeDefinition>;
}

export const extractDynamoDBPrimaryKey = (table: Optional<DynamoDBTable>): ExtractDynamoDBPrimaryKeyReturn => {
  const hashKey = table?.KeySchema?.find((s) => s.KeyType === 'HASH');
  const rangeKey = table?.KeySchema?.find((s) => s.KeyType === 'RANGE');

  const hashKeyDef = table?.AttributeDefinitions?.find((a) => a.AttributeName === hashKey?.AttributeName);
  const rangeKeyDef = table?.AttributeDefinitions?.find((a) => a.AttributeName === rangeKey?.AttributeName);

  return { hashKey, hashKeyDef, rangeKey, rangeKeyDef };
};

export const buildDynamoDBPrimaryKey = (
  table: Optional<DynamoDBTable>,
  hashKeyValue: Optional<string | boolean | number>,
  sortKeyValue?: Optional<string | boolean | number>,
): Record<string, string> => {
  const { hashKey, hashKeyDef, rangeKey, rangeKeyDef } = extractDynamoDBPrimaryKey(table);

  if (!hashKey || !hashKeyDef) throw new Error('Missing hash key or hash key definition');

  return {
    [hashKey.AttributeName]: restoreDynamoDBType(hashKeyDef.AttributeType, hashKeyValue),
    ...(rangeKey &&
      rangeKeyDef && {
        [rangeKey.AttributeName]: restoreDynamoDBType(rangeKeyDef.AttributeType, sortKeyValue),
      }),
  };
};

/**
 * Wrap a primary-key value in its explicit DynamoDB attribute-value descriptor
 * (e.g. `{ S: "abc" }`, `{ N: "123" }`). This is the wire format expected by the
 * low-level DynamoDB `getItem`/`putItem` APIs, as opposed to the plain values used
 * by the higher-level DocumentClient.
 */
const toTypedAttributeValue = (
  type: Optional<string>,
  value: Optional<string | boolean | number>,
): DynamoDBAttributeValue => {
  const resolvedType = type || 'S';
  // The low-level API always represents numbers as strings.
  if (resolvedType === 'N') return { N: value?.toString() } as DynamoDBAttributeValue;
  return { [resolvedType]: value } as DynamoDBAttributeValue;
};

/**
 * Build a primary key in the explicit attribute-value (typed) form required by the
 * low-level DynamoDB `getItem`/`putItem`/`deleteItem` APIs.
 */
export const buildDynamoDBTypedPrimaryKey = (
  table: Optional<DynamoDBTable>,
  hashKeyValue: Optional<string | boolean | number>,
  sortKeyValue?: Optional<string | boolean | number>,
): Record<string, DynamoDBAttributeValue> => {
  const { hashKey, hashKeyDef, rangeKey, rangeKeyDef } = extractDynamoDBPrimaryKey(table);

  if (!hashKey || !hashKeyDef) throw new Error('Missing hash key or hash key definition');

  return {
    [hashKey.AttributeName]: toTypedAttributeValue(hashKeyDef.AttributeType, hashKeyValue),
    ...(rangeKey &&
      rangeKeyDef && {
        [rangeKey.AttributeName]: toTypedAttributeValue(rangeKeyDef.AttributeType, sortKeyValue),
      }),
  };
};

/**
 * Convert a typed DynamoDB item (attribute-value form, e.g. `{ tags: { SS: [...] } }`)
 * into its plain, human-friendly representation (`{ tags: [...] }`) for display/editing.
 *
 * Note this is intentionally lossy for display purposes: sets render as arrays and binary
 * as `{ type: "Buffer", data: [...] }`. The original typed item must be retained so the
 * merge in `dynamoDBPlainToTyped` can restore the exact types on save.
 */
export const dynamoDBTypedToPlain = (item: Optional<DynamoDBTypedItem>): DynamoDBPlainItem =>
  item ? (DynamoDB.Converter.unmarshall(item) as DynamoDBPlainItem) : {};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.length > 0 && value.every((item) => typeof item === 'string');

const isNumberArray = (value: unknown): value is number[] =>
  Array.isArray(value) && value.length > 0 && value.every((item) => typeof item === 'number' && Number.isFinite(item));

/**
 * A Set (`SS`/`NS`) renders as a plain array in the editor — indistinguishable from a List —
 * so editing its items would naively re-marshal to `L`. If the attribute was originally a
 * string/number set and the edited value still fits that set type, keep it as a set.
 * Returns `undefined` when no coercion applies (caller falls back to the marshalled value).
 */
const preserveEditedSetType = (
  referenceValue: Optional<DynamoDBAttributeValue>,
  plainValue: unknown,
): Optional<DynamoDBAttributeValue> => {
  if (!referenceValue) return undefined;
  if ('SS' in referenceValue && isStringArray(plainValue)) return { SS: plainValue } as DynamoDBAttributeValue;
  if ('NS' in referenceValue && isNumberArray(plainValue)) {
    return { NS: plainValue.map((item) => item.toString()) } as DynamoDBAttributeValue;
  }
  return undefined;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Merge a single edited plain value back into its typed attribute-value form, using the
 * original typed value as a reference to preserve types that plain JSON cannot express.
 *
 *  - unchanged value          -> reuse the original typed value verbatim (exact type kept);
 *  - edited Map (`M`)         -> recurse per key, so untouched nested set/binary survive;
 *  - edited List (`L`)        -> recurse per index;
 *  - edited string/number set -> keep as a set when the new value still fits it;
 *  - everything else          -> marshal the plain value (binary cannot be edited as JSON).
 */
const mergeEditedValue = (
  plainValue: unknown,
  referenceValue?: Optional<DynamoDBAttributeValue>,
): DynamoDBAttributeValue => {
  if (referenceValue) {
    // normalize through the same JSON round-trip the editor content went through
    const referencePlain = JSON.parse(JSON.stringify(DynamoDB.Converter.output(referenceValue)));
    if (isEqual(plainValue, referencePlain)) return referenceValue;

    if ('M' in referenceValue && isPlainObject(plainValue)) {
      const referenceMap = (referenceValue.M ?? {}) as Record<string, DynamoDBAttributeValue>;
      const merged = Object.keys(plainValue).reduce<Record<string, DynamoDBAttributeValue>>((acc, key) => {
        acc[key] = mergeEditedValue(plainValue[key], referenceMap[key]);
        return acc;
      }, {});
      return { M: merged } as DynamoDBAttributeValue;
    }

    if ('L' in referenceValue && Array.isArray(plainValue)) {
      const referenceList = (referenceValue.L ?? []) as DynamoDBAttributeValue[];
      return {
        L: plainValue.map((item, index) => mergeEditedValue(item, referenceList[index])),
      } as DynamoDBAttributeValue;
    }

    const preservedSet = preserveEditedSetType(referenceValue, plainValue);
    if (preservedSet) return preservedSet;
  }

  return DynamoDB.Converter.input(plainValue) as DynamoDBAttributeValue;
};

/**
 * Convert a plain (edited) item back into the typed attribute-value form for `putItem`.
 *
 * Plain JSON cannot distinguish a Set from a List or Binary from a Map, so marshalling
 * the plain form alone would corrupt `B`/`SS`/`NS`/`BS` attributes. Using the originally
 * loaded item (`referenceTyped`) as a reference, types are preserved recursively: untouched
 * attributes keep their exact type at any nesting depth, and an edited string/number set is
 * kept as a set when the new value still fits. Binary still cannot be edited as plain JSON.
 */
export const dynamoDBPlainToTyped = (
  plainItem: DynamoDBPlainItem,
  referenceTyped?: Optional<DynamoDBTypedItem>,
): DynamoDBTypedItem =>
  Object.keys(plainItem).reduce<DynamoDBTypedItem>((acc, key) => {
    acc[key] = mergeEditedValue(plainItem[key], referenceTyped?.[key]);
    return acc;
  }, {});
